import { useEffect, useState } from "react";

/**
 * Piqpath "typing" indicator — three staggered dots colored to match
 * the three brand category cards (flights / hotels / packages).
 *
 *   Dot 1  Flights   #2563EB
 *   Dot 2  Hotels    #16A34A
 *   Dot 3  Packages  #7C3AED
 *
 * Reduced-motion users see the same three dots, static (no pulse).
 * Public API (component name, default export, props, `data-testid`)
 * is unchanged so every call-site keeps working.
 */

const DOTS = [
    { color: "#2563EB", delay: "0s" },     // Flights
    { color: "#16A34A", delay: "0.16s" },  // Hotels
    { color: "#7C3AED", delay: "0.32s" },  // Packages
];

export default function TypingDots({ className = "" }) {
    const [reduceMotion, setReduceMotion] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReduceMotion(mq.matches);
        const handler = (e) => setReduceMotion(e.matches);
        mq.addEventListener?.("change", handler);
        return () => mq.removeEventListener?.("change", handler);
    }, []);

    return (
        <>
            <style>{`
                @keyframes piq-typing-pulse {
                    0%, 60%, 100% {
                        transform: translateY(0);
                        opacity: 0.35;
                    }
                    30% {
                        transform: translateY(-3px);
                        opacity: 1;
                    }
                }
            `}</style>
            <span
                role="status"
                aria-label="Loading"
                data-testid="typing-dots"
                className={`inline-flex h-4 items-center justify-center gap-[5px] align-middle ${className}`}
            >
                {DOTS.map((d) => (
                    <span
                        key={d.color}
                        className="block h-[8px] w-[8px] rounded-full"
                        style={
                            reduceMotion
                                ? { background: d.color, opacity: 0.85 }
                                : {
                                      background: d.color,
                                      animation:
                                          "piq-typing-pulse 1.1s ease-in-out infinite",
                                      animationDelay: d.delay,
                                  }
                        }
                    />
                ))}
            </span>
        </>
    );
}
